import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Upload, FileText, CheckCircle2, AlertCircle } from "lucide-react";
import { parseCSV, mapCSVToLeads } from "@/lib/importCsv";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { sanitizeText, sanitizeObject } from "@/lib/sanitize";
import { getCurrentUser } from "@/lib/authUser";

interface ImportLeadsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLeadsImported: () => void;
  groupId?: string | null;
  columnConfig: Array<{ id: string; name: string }>;
}

const ImportLeadsDialog = ({ 
  open, 
  onOpenChange, 
  onLeadsImported, 
  groupId,
  columnConfig 
}: ImportLeadsDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error("Invalid file type", { description: "Please select a CSV file" });
      return;
    }

    setSelectedFile(file);
    
    try {
      const text = await file.text();
      const parsed = parseCSV(text);
      const mapped = mapCSVToLeads(parsed, columnConfig);
      setPreviewData(mapped.slice(0, 3));
    } catch (error: any) {
      toast.error("Error parsing CSV", { description: error.message });
      setSelectedFile(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setLoading(true);

    try {
      const user = await getCurrentUser();
      if (!user) throw new Error("Not authenticated");

      const text = await selectedFile.text();
      const parsed = parseCSV(text);
      const leads = mapCSVToLeads(parsed, columnConfig);

      if (leads.length === 0) {
        throw new Error("No valid leads found in CSV");
      }

      const leadsToInsert = leads.map(lead => ({
        ...lead,
        lead_name: sanitizeText(lead.lead_name || '', 500),
        company: lead.company ? sanitizeText(lead.company, 500) : null,
        email: lead.email ? sanitizeText(lead.email, 254) : null,
        phone: lead.phone ? sanitizeText(lead.phone, 30) : null,
        custom_fields: lead.custom_fields ? sanitizeObject(lead.custom_fields as Record<string, any>) : null,
        user_id: user.id,
        group_id: groupId || null,
        last_refreshed_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
      }));

      const { error } = await supabase
        .from("leads")
        .insert(leadsToInsert);

      if (error) throw error;

      toast("Import successful!", {
        description: `Successfully imported ${leads.length} lead${leads.length !== 1 ? 's' : ''}`,
      });

      setSelectedFile(null);
      setPreviewData(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onOpenChange(false);
      onLeadsImported();
    } catch (error: any) {
      toast.error("Import failed", {
        description: error.message || "Failed to import leads",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setSelectedFile(null);
      setPreviewData(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Import Leads from CSV
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file to import leads into your lead sheet
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="space-y-2">
              <p className="font-semibold">Tips for best results:</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Configure your columns in Sheet Setup before importing</li>
                <li>Format your CSV headers to match your lead sheet columns as closely as possible</li>
                <li>Standard columns: Name, Company, Email, Phone, Status, Priority, Value, Source, Notes, Next Action</li>
                <li>Custom columns should match exactly (case-insensitive)</li>
                <li>Avoid formulas, special formatting, or merged cells</li>
                <li>First row should contain column headers</li>
              </ul>
            </AlertDescription>
          </Alert>

          <div className="flex flex-col gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              id="csv-upload"
            />
            <label htmlFor="csv-upload">
              <Button type="button" variant="outline" className="w-full" asChild>
                <span className="flex items-center gap-2 cursor-pointer">
                  <FileText className="h-4 w-4" />
                  {selectedFile ? selectedFile.name : "Choose CSV File"}
                </span>
              </Button>
            </label>
          </div>

          {previewData && previewData.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Preview of first {previewData.length} lead{previewData.length !== 1 ? 's' : ''}
              </div>
              <div className="border rounded-lg p-4 bg-muted/20 max-h-64 overflow-auto">
                {previewData.map((lead, idx) => (
                  <div key={idx} className="mb-3 pb-3 border-b last:border-b-0 last:mb-0 last:pb-0">
                    <div className="font-semibold">{lead.lead_name}</div>
                    <div className="text-sm text-muted-foreground space-y-1 mt-1">
                      {lead.company && <div>Company: {lead.company}</div>}
                      {lead.email && <div>Email: {lead.email}</div>}
                      {lead.phone && <div>Phone: {lead.phone}</div>}
                      {lead.custom_fields && Object.keys(lead.custom_fields).length > 0 && (
                        <div className="text-xs">
                          Custom fields: {Object.keys(lead.custom_fields).join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => handleDialogClose(false)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleImport} disabled={!selectedFile || loading} className="gap-2">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Importing..." : "Import Leads"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportLeadsDialog;
